/**
 * Internal actions for directly managing the outbound HTTP requests + state updates
 * with the store. To be used by other public, UI centric actions, not to be used directly
 * by components outside of the store.
 */
import _ from 'lodash';
import Q from 'q';

import type { ActionContext } from 'js/lib/ActionContext';

import { partnerRoleTemplates, staffMemberships } from '../apis';
import { EVENT_NAMES } from '../constants';
import typeToScopeTypeName from '../typeToScopeTypeName';
import type { EmailableInterface, IdentifiableInterface, ObjectId, StaffMembershipType } from '../types';

/**
 * Create new memberships for users via their emails.
 *
 * Any user emails that are not coursera users will be returned, to be handled elsewhere.
 */
export const create = (
  /** Provided by fluxible. */
  actionContext: ActionContext,
  {
    fromId,
    /** List of role ids to assign to users, via email. */
    roles,
    type,
    /** List of user emails. */
    users,
    // API DI.
    _api = staffMemberships,
  }: {
    fromId: string | number;
    roles: Array<string>;
    type: keyof typeof StaffMembershipType;
    users: Array<EmailableInterface>;
    _api?: {
      get(args: { ids: string | Array<string> }): Q.Promise<staffMemberships.StaffMembershipsAPIGetResponseInterface>;
      create(args: {
        memberships: Array<staffMemberships.StaffMembershipsAPICreateInterface>;
      }): Q.Promise<staffMemberships.StaffMembershipsAPICreateResponseInterface>;
    };
  }
): Q.Promise<Array<Array<string>>> => {
  actionContext.dispatch(EVENT_NAMES.DATA, { creating: true });
  return Q(
    _api.create({
      memberships: _.flatten(
        _.map(users, (user) =>
          _.map(roles, (role) => ({
            email: user.email,
            roleId: role,
            scope: {
              typeName: typeToScopeTypeName[type],
              id: fromId,
            },
          }))
        )
      ),
    })
  )
    .then(async (response) => {
      const { newMembershipIds, newUserEmails } = _.reduce(
        _.get(response, 'result'),
        (opts: { newMembershipIds: Array<string>; newUserEmails: Array<string> }, value, email) => {
          if (value.length) {
            opts.newMembershipIds = opts.newMembershipIds.concat(value); // eslint-disable-line no-param-reassign
          } else {
            opts.newUserEmails.push(email);
          }
          return opts;
        },
        { newMembershipIds: [], newUserEmails: [] }
      );

      const newMembershipsGetResponse = await (newMembershipIds.length
        ? _api.get({ ids: newMembershipIds })
        : { elements: [] });
      let existingUserEmails: Array<string> = [];
      // Update existing state only if we have new memberships.
      if (_.get(newMembershipsGetResponse, 'elements.length')) {
        const dataState = actionContext.getStore('StaffManagementStoreV2').data();
        const linkedUsers = _.keyBy(_.get(newMembershipsGetResponse, ['linked', 'users.v1']), 'id');
        existingUserEmails = _.map(linkedUsers, (user) => user.email);
        actionContext.dispatch(EVENT_NAMES.DATA, {
          memberships: { ...dataState.memberships, ..._.keyBy(_.get(newMembershipsGetResponse, 'elements'), 'id') },
          roles: {
            ...dataState.roles,
            ..._.keyBy(
              _.map(_.get(newMembershipsGetResponse, ['linked', 'partnerRoleTemplates.v1']), (rt) => ({
                ...rt,
                name: rt.displayName,
              })),
              'id'
            ),
          },
          users: { ...dataState.users, ...linkedUsers },
        });
      }

      // Return list of existing users and non-existing users.
      return [existingUserEmails, newUserEmails];
    })
    .finally(() => {
      actionContext.dispatch(EVENT_NAMES.DATA, { creating: false });
    });
};

/**
 * Delete memberships.
 *
 * Store `data` only modified on successful completion of request from server.
 * Any non-2xx response indicates no backend modifications and no deletions occurred.
 * Busy state expressed by `activity.deleting`. Errors will be added to the `errors` queue.
 */
export const del = (
  /** Provided by fluxible. */
  actionContext: ActionContext,
  /** Membership id(s) to delete. */
  {
    ids = [],
    // API DI.
    _api = staffMemberships,
  }: {
    ids?: ObjectId | Array<ObjectId>;
    _api?: {
      del(args: { ids: ObjectId | Array<ObjectId> }): Q.Promise<void>;
    };
  } = {}
): Q.Promise<void> => {
  actionContext.dispatch(EVENT_NAMES.DATA, { deleting: true });
  return Q(
    _api
      .del({ ids })
      .then(() => {
        // If a user runs out of membership links, we also delete the user from our local state
        // as they are no longer part of the feature (partner, program, course) context.
        const deletedIds = Array.isArray(ids) ? ids : [ids];

        const membershipsCurrent = actionContext.getStore('StaffManagementStoreV2').data('memberships');
        const usersNotDeleted = {};
        const memberships = _.reduce(
          membershipsCurrent,
          (ms, membership) => {
            // Remove any specifically deleted memberships.
            if (deletedIds.indexOf(membership.id) === -1) {
              // Disabling eslint here as modifying an input param for aggregation is exactly what reduce is for.
              // @ts-expect-error ts-migrate(7053) FIXME: Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
              ms[membership.id] = membership; // eslint-disable-line no-param-reassign
              // @ts-expect-error ts-migrate(7053) FIXME: Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
              usersNotDeleted[membership.userId] = true;
            }
            return ms;
          },
          {}
        );

        const usersCurrent = actionContext.getStore('StaffManagementStoreV2').data('users');
        const users = _.reduce(
          usersCurrent,
          (us, u) => {
            // @ts-expect-error ts-migrate(7053) FIXME: Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
            if (usersNotDeleted[u.id]) {
              // Disabling eslint here as modifying an input param for aggregation is exactly what reduce is for.
              // @ts-expect-error ts-migrate(7053) FIXME: Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
              us[u.id] = u; // eslint-disable-line no-param-reassign
            }
            return us;
          },
          {}
        );

        actionContext.dispatch(EVENT_NAMES.DATA, {
          memberships,
          users,
        });
      })
      .finally(() => {
        actionContext.dispatch(EVENT_NAMES.DATA, { deleting: false });
      })
  );
};

/**
 * Load/refresh staff memberships data in store.
 *
 * After successful load initializing will be false and data will be populated.
 * After unsuccessful load, the error will be stored in the state and rethrown. No other store
 * state will be modified.
 */
export const load = (
  /** Provided by fluxible. */
  actionContext: ActionContext,
  /**
   * Id and type of entity from which to load membeships.
   * Optional sessionId for courses.
   */
  {
    fromId,
    sessionId,
    type,
    // API DI.
    _api = staffMemberships,
  }: {
    fromId: string | number;
    sessionId?: number | string;
    type: keyof typeof StaffMembershipType;
    _api?: {
      getAll(args: {
        fromId: string | number;
        fields?: string;
        includes?: string;
        sessionId?: string | number;
        type: keyof typeof StaffMembershipType;
      }): Q.Promise<staffMemberships.StaffMembershipsAPIGetResponseInterface>;
    };
  }
): Q.Promise<void> => {
  actionContext.dispatch(EVENT_NAMES.DATA, { loading: true });
  return _api
    .getAll({ fromId, sessionId, type })
    .then((response) => {
      actionContext.dispatch(EVENT_NAMES.DATA, {
        error: undefined,
        initializing: false,

        memberships: _.keyBy(_.get(response, 'elements'), 'id'),
        // The required fields for roles changed during development. As this app only
        // reads roles, map the name to the displayName.
        roles: _.keyBy(
          _.map(_.get(response, ['linked', 'partnerRoleTemplates.v1']), (rt) => ({ ...rt, name: rt.displayName })),
          'id'
        ),
        users: _.keyBy(_.get(response, ['linked', 'users.v1']), 'id'),
      });
    })
    .catch((error) => {
      actionContext.dispatch(EVENT_NAMES.DATA, { error });
      throw error;
    })
    .finally(() => {
      actionContext.dispatch(EVENT_NAMES.DATA, { loading: false });
    });
};

/**
 * Load/refresh partner role templates.
 *
 * After successful load initializing will be false and data will be populated.
 * After unsuccessful load, the error will be stored in the state and rethrown. No other store
 * state will be modified.
 */
export const loadPartnerRoleTemplates = (
  /** Provided by fluxible. */
  actionContext: ActionContext,
  /**
   * Id and type of entity from which to load memberships.
   */
  {
    partnerId,
    type,
    // API DI.
    _api = partnerRoleTemplates,
  }: {
    partnerId: number | string;
    type: keyof typeof StaffMembershipType;
    _api?: {
      getAll(args: {
        partnerId: string | number;
      }): Q.Promise<partnerRoleTemplates.PartnerRoleTemplatesAPIGetResponseInterface>;
    };
  }
): Q.Promise<void> => {
  actionContext.dispatch(EVENT_NAMES.PARTNER_ROLE_TEMPLATES, { loading: true });
  return _api
    .getAll({ partnerId })
    .then((response) => {
      const matchingType = typeToScopeTypeName[type];
      // Map into format already being used, inherently scoped to the type we
      // are working with.
      const templates = _.keyBy(
        _.filter(
          _.get(response, 'elements'),
          (element) => _.get(element, 'hydrateRolesForEntityTypes[0]') === matchingType
        ).map((element) => ({
          id: element.id,
          name: element.displayName,
        })),
        'id'
      );
      actionContext.dispatch(EVENT_NAMES.PARTNER_ROLE_TEMPLATES, { initializing: false, error: undefined, templates });
    })
    .catch((error) => {
      actionContext.dispatch(EVENT_NAMES.PARTNER_ROLE_TEMPLATES, { error });
      throw error;
    })
    .finally(() => {
      actionContext.dispatch(EVENT_NAMES.PARTNER_ROLE_TEMPLATES, { loading: false });
    });
};

/**
 * Reset the state of the store.
 */
export const reset = (
  /** Provided by fluxible. */
  actionContext: ActionContext
): void => {
  actionContext.dispatch(EVENT_NAMES.RESET);
};

/**
 * Update the set of memberships for a set of users.
 */
export const update = (
  /** Provided by fluxible. */
  actionContext: ActionContext,
  {
    fromId,
    /** List of role ids to set for a given set of users. */
    roles,
    type,
    /** List of users. */
    users,
    // API DI.
    _api = staffMemberships,
  }: {
    fromId: string | number;
    roles: Array<string>;
    type: keyof typeof StaffMembershipType;
    users: Array<IdentifiableInterface>;
    _api?: {
      update(args: {
        fromId: string | number;
        memberships: Array<staffMemberships.StaffMembershipsAPIUpdateInterface>;
        type: keyof typeof StaffMembershipType;
      }): Q.Promise<staffMemberships.StaffMembershipsAPIUpdateResponseInterface>;
    };
  }
): Q.Promise<void> => {
  actionContext.dispatch(EVENT_NAMES.DATA, { updating: true });
  return Q(
    _api.update({
      fromId,
      memberships: _.flatten(
        _.map(users, (user) =>
          _.map(roles, (role) => ({
            roleId: role,
            scope: {
              typeName: typeToScopeTypeName[type],
              id: fromId,
            },
            userId: user.id as number,
          }))
        )
      ),
      type,
    })
  )
    .then(({ deletedMemberships, addedMemberships }) => {
      const membershipsCurrent = _.clone(actionContext.getStore('StaffManagementStoreV2').data('memberships'));
      let memberships = _.reduce(
        deletedMemberships,
        (ms, deletedMembership) => {
          delete ms[deletedMembership.id]; // eslint-disable-line no-param-reassign
          return ms;
        },
        membershipsCurrent
      );
      memberships = _.reduce(
        addedMemberships,
        (ms, addedMembership) => {
          ms[addedMembership.id] = addedMembership; // eslint-disable-line no-param-reassign
          return ms;
        },
        memberships
      );

      actionContext.dispatch(EVENT_NAMES.DATA, {
        memberships,
      });
    })
    .finally(() => {
      actionContext.dispatch(EVENT_NAMES.DATA, { updating: false });
    });
};
